import React, { ComponentProps, ReactNode } from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import Button from '@adsk/alloy-react-button';
import {
  FunnelIcon,
  RenderIconType,
  XCircleFilledIcon,
} from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  stylePropType,
  StylableComponent,
  StyleProp,
  flattenStyle,
} from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

const StyledFilterButton = styledC(Button)`
  ${(props) =>
    !props.disabled &&
    `
  &:hover {
    box-shadow: none;
  }
  &:focus {
    box-shadow: ${theme.halos.focus};
  }
  &:active {
    box-shadow: ${theme.halos.active};
  }
`}
`;

const StyledClearButton = styledC(StyledFilterButton)`
  width: 36px;
  height: 36px;
  border-top-left-radius: ${theme.borderRadius.none}px;
  border-bottom-left-radius: ${theme.borderRadius.none}px;
  border-left: ${theme.borderRadius.none}px;
`;

const StyledContainer = styledC.div<{ $disabled: boolean | undefined }>`
  display: inline-flex;
  align-items: center;
  transition: ${theme.transitions.create(['box-shadow', 'border'])};
    ${(props) =>
      !props.$disabled &&
      `
      &:hover {
        box-shadow: ${theme.halos.hover};
      },
    `};
`;

type FilterButtonTextProps = {
  customFilterText?: ReactNode;
  filtersCount?: number;
  minimized?: boolean;
  T: ReturnType<typeof useLocale>['T'];
};

type FilterButtonProps = Omit<
  StylableComponent<HTMLButtonElement>,
  'children'
> & {
  filtersOpen?: boolean;
  clearFilters: ComponentProps<typeof Button>['onClick'];
  customFilterText?: ReactNode;
  customClearFilterText?: ReactNode;
  filtersCount?: number;
  minimized?: boolean;
  disabled?: boolean;
  renderFilterButtonIcon?: RenderIconType;
  renderFilterButtonText?: (props: FilterButtonTextProps) => ReactNode;
  buttonStyle?: StyleProp;
};

const getFilterButtonText = ({
  filtersCount,
  minimized,
  customFilterText,
  T,
}: FilterButtonTextProps) => {
  if (!filtersCount) {
    return null;
  }

  if (minimized) {
    return `(${filtersCount})`;
  }

  return (
    customFilterText || T.translate('FILTER_INDICATOR', { count: filtersCount })
  );
};

/**
 * Filter Button.
 * Used to handle filters panel state.
 */
const FilterButton = React.forwardRef<HTMLDivElement, FilterButtonProps>(
  (
    {
      style,
      className,
      filtersOpen,
      filtersCount,
      clearFilters,
      customFilterText,
      customClearFilterText,
      minimized,
      disabled,
      renderFilterButtonText = getFilterButtonText,
      renderFilterButtonIcon = FunnelIcon.renderIcon,
      buttonStyle: buttonStyleProp,
      ...props
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

    const hasFilters = Boolean(filtersCount);
    const buttonProps: ComponentProps<typeof Button> = {
      'aria-label': T.translate('FILTER')?.toString(),
      renderIcon: renderFilterButtonIcon,
      selected: filtersOpen,
      disabled,
      ...props,
    };

    const buttonStyle: StyleProp = {
      ...(filtersCount && {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      }),
      fontVariantNumeric: 'tabular-nums',
      ...(!filtersOpen &&
        !disabled && {
          '&:hover': {
            background: theme.tokens.colors.background.neutral.hover.value,
          },
        }),
    };

    const clearFiltersText = disabled
      ? ''
      : customClearFilterText || T.translate('CLEAR_ALL_FILTERS');

    return hasFilters ? (
      <StyledContainer
        ref={ref}
        className={className}
        $disabled={disabled}
        style={flattenStyle(style)}
      >
        <StyledFilterButton
          style={[buttonStyle, buttonStyleProp]}
          {...buttonProps}
        >
          {renderFilterButtonText({
            filtersCount,
            minimized,
            customFilterText,
            T,
          })}
        </StyledFilterButton>

        <Tooltip
          content={clearFiltersText}
          placement={Tooltip.PLACEMENTS.TOP_END}
        >
          <StyledClearButton
            aria-label={clearFiltersText?.toString()}
            renderIcon={XCircleFilledIcon.renderIcon}
            onClick={clearFilters}
            disabled={disabled}
          />
        </Tooltip>
      </StyledContainer>
    ) : (
      <StyledFilterButton
        ref={ref}
        className={className}
        style={[buttonStyle, style, buttonStyleProp]}
        {...buttonProps}
      >
        {renderFilterButtonText({
          filtersCount,
          minimized,
          customFilterText,
          T,
        })}
      </StyledFilterButton>
    );
  },
);

FilterButton.displayName = 'FilterButton';

FilterButton.propTypes = {
  /**
   * Styles applied to the root element
   */
  style: stylePropType,
  /**
   * Class applied to the root element
   */
  className: PropTypes.string,
  /**
   * Whether or not filter panel is open.
   */
  filtersOpen: PropTypes.bool.isRequired,
  /**
   * Callback to toggle filter panel open/close.
   */
  onClick: PropTypes.func.isRequired,
  /**
   * Callback to clear filters.
   */
  clearFilters: PropTypes.func.isRequired,
  /**
   * Number of active filters.
   */
  filtersCount: PropTypes.number,
  /**
   * Whether the button in minimized mode.
   */
  minimized: PropTypes.bool,
  /**
   * Disable button functionality
   */
  disabled: PropTypes.bool,
  /**
   * Custom filter text overwrite
   */
  customFilterText: PropTypes.any,
  /**
   * Custom the tooltip text for the clear filter button
   */
  customClearFilterText: PropTypes.any,
  /**
   * Render props to generate filter text
   */
  renderFilterButtonText: PropTypes.func,
  /**
   * Render props to render filter button icon
   */
  renderFilterButtonIcon: PropTypes.func,
  /**
   * Style applied to the button
   */
  buttonStyle: stylePropType,
};

export default FilterButton;
